import React from 'react';
import { useTheme } from 'styled-components';

import { testimonialOptions } from '../../components/testimoninal/testimoninal';
import { TextWithHighlight } from '../../components/textWithHighlight/textWithHighlight';
import { getEnv } from '../../lib/utils/getEnv';
import { BusinessCompetitor } from '../businessCompetitors/businessCompetitors';
import { SignUpProcessTitleContainer } from '../businessCompetitors/components/signUpProcess/signUpProcess.styled';

const { VITE_WEB_BASE_URL, VITE_HELP_CENTRE_URL } = getEnv();

const highlevelBenefitsConfig = [
  {
    id: 'software-without-limits',
    title: 'Software without limits',
    icon: 'Rocket',
    description: 'Expand your catalogue of services and locations, all managed in one central place.',
  },
  {
    id: 'reduce-no-show',
    title: 'Reduce no-shows',
    icon: 'NoShow',
    description: 'Enable reminders and take upfront payments to tackle cancellations without extra admin.',
  },
  {
    id: 'easy-tp-use-design',
    title: 'Easy-to-use design',
    icon: 'Tap',
    description: 'Access bookings and client information in a few clicks through our simple calendar.',
  },
];

const savvyTestimoninalConfig = [
  {
    children: testimonialOptions.pet_palace,
    color: '#f5c14f',
  },
  {
    children: testimonialOptions.whitegates_grooming,
    color: '#f86e6a',
  },
  {
    children: testimonialOptions.groomed_by_nic,
    color: '#90cfd1',
  },
  {
    children: testimonialOptions.mias_paw_spa,
    color: '#f5a046',
  },
  {
    children: testimonialOptions.pawfect_place,
    color: '#8cc4b8',
  },
];

export const savvyFaqsConfig = [
  {
    question: 'Is Pata better than Savvy Pet Spa?',
    answer: () => (
      <>
        <p>
          We might be biased, but yes! Pata booking software is easy to navigate, helps grow your network of clients
          through our online marketplace, and offers subscription-free access to features, no matter your business size
          or speciality.
        </p>

        <p>
          Whether you’re a dog groomer or provide other pet care services, our flexible tools and easy-to-use app are
          designed with professionals like you in mind. Plus, our in-house team is always on hand to respond to feedback
          and adapt our platform to suit your needs.
        </p>
      </>
    ),
  },
  {
    question: 'Does Pata have a free plan?',
    answer: () => (
      <>
        <p>
          Yes, it does! In fact, Pata is subscription-free, giving you unlimited access to innovative tools that
          simplify your admin with no upfront or monthly costs — perfect for trying out our booking platform at your own
          pace.
        </p>

        <p>
          When you’re ready to grow your business further, we can support your goals with additional SMS notifications
          and{' '}
          <a href={`${VITE_WEB_BASE_URL}/business/pata-pay`} target="_blank">
            payment processing
          </a>{' '}
          at low fees, but these features are entirely optional.
        </p>
      </>
    ),
  },
  {
    question: 'Can I import my data from Savvy Pet Spa?',
    answer: () => (
      <>
        <p>
          Of course, and we’re here to make that transition as smooth as possible. We can help import key data from
          Savvy into Pata’s booking software, including client information, appointments, services, and staff rotas.
        </p>
      </>
    ),
  },
  {
    question: 'Is Pata suitable for solo groomers and small teams?',
    answer: () => (
      <>
        <p>
          With Pata, businesses of any size can thrive and spend less time on admin — from small teams to multi-location
          grooming salons. There are no upfront or monthly fees to use our pet care software, meaning solo or mobile
          groomers can access the same powerful tools that larger businesses use.
        </p>

        <p>
          This includes 24/7 online bookings, an intuitive calendar, automated client messages and more. All our smart
          and flexible features are designed to simplify your day-to-day tasks and help your business grow.
        </p>
      </>
    ),
  },
  {
    question: 'Does Pata support online payments and deposits?',
    answer: () => (
      <>
        <p>
          Taking upfront payments and deposits online couldn’t be easier with{' '}
          <a href={`${VITE_WEB_BASE_URL}/business/pata-pay`} target="_blank">
            PataPay
          </a>
          . Set-up is quick, helping to reduce no-shows and protect you against late cancellations from the start. You
          also have the option to store card details for future no-show fees.
        </p>

        <p>
          If your clients prefer to settle up in person, we’ll send out a card reader. After a payment has been made,
          it’s integrated into our booking platform where you can securely track each transaction in real time, keeping
          you in control of your income.
        </p>
      </>
    ),
  },
  {
    question: 'What types of pet care businesses can use Pata?',
    answer: () => (
      <>
        <p>
          In short, any and all! From groomers, walkers and sitters to dog trainers, vets and more, Pata booking
          platform is the perfect tool for any pet care business.
        </p>

        <p>
          Whether you’re a solo dog groomer who also offers boarding or you run a multi-location clinic, Pata helps pros
          like you to take bookings, organise schedules, minimise no-shows, and expand your client base with one simple
          app.
        </p>
      </>
    ),
  },
];

const savvyHeroConfig = {
  title: 'Looking to swap from Savvy to a more intuitive booking software?',
  subTitle:
    'Join the movement of UK professionals switching to Pata, the easy-to-use platform with unlimited access to powerful features to grow your pet care business.',
};

export const savvyFeaturesTiles = [
  {
    id: 'payments',
    title: 'Integrated payments',
    imageUrl: 'images/image-lady-with-dog-using-laptop.jpeg',
    icon: 'OnlineBooking',
    callout:
      'Offer a seamless experience for customers while protecting your time and income with just one account for deposits and in-person payments.',
    features: [
      {
        id: 'upfront_payments',
        title: 'Upfront payments',
        description:
          'Reduce no-shows and late cancellations by taking partial deposits or full payments for services during booking, protecting your time.',
      },
      {
        id: 'card_on_file',
        title: 'Card on file',
        description:
          'Securely store payment details to provide a smoother checkout experience for clients and cover yourself for any future cancellation or no-show fees.',
      },
      {
        id: 'terminal',
        title: 'Terminal',
        description:
          'Give customers even more ways to pay with a sleek card reader that syncs transactions into our grooming salon booking system.',
      },
      {
        id: 'late_cancellation',
        title: 'Late cancellation and no-show policies',
        description:
          'Set clear terms for when sessions can be cancelled and charge fees if those conditions aren’t met, keeping you in control of your income.',
      },
    ],
  },
  {
    id: 'marketing',
    title: 'Marketing',
    icon: 'Calendar',
    imageUrl: 'images/image-laptop-with-calendar-app.png',
    callout:
      'Grow your client base, upsell services and build greater connections with every customer, not just those with an app — all through one centralised booking platform.',
    features: [
      {
        id: 'campaigns',
        title: 'Email and SMS campaigns',
        description:
          'Send branded messages in minutes, straight from your Pata account — keeping clients updated and building excitement to drive sales.',
      },
      {
        id: 'marketplace',
        title: 'Pata Marketplace',
        description:
          'Improve your online presence with a free Marketplace profile and get discovered by pet parents searching for local grooming services.',
      },
      {
        id: 'reviews',
        title: 'Reviews',
        description:
          'Send automated requests for reviews while you focus on the next furry client, showcasing your skills and building trust with new customers.',
      },
    ],
  },
  {
    id: 'catalogue',
    title: 'Supercharged catalogue',
    icon: 'Catalogue',
    imageUrl: 'images/image-dog-getting-face-trim.jpeg',
    callout:
      'Easily adapt how services are configured in line with changing business needs, and capitalise on opportunities for growth thanks to integrated promotions.',
    features: [
      {
        id: 'service',
        title: 'Services',
        description:
          'Run your business your way with highly configurable services, including block times, booking limitations, flexible pricing options, and more.',
      },
      {
        id: 'memberships',
        title: 'Memberships',
        description:
          'Build loyalty with clients and enjoy some guaranteed revenue, offering service bundles or promotions that encourage multi-session bookings.',
      },
      {
        id: 'products',
        title: 'Products',
        description:
          'Inspire pet parents to take a slice of the salon home by upselling products you know and love, powered by our flexible payment methods.',
      },
      {
        id: 'add-ons',
        title: 'Add on',
        description:
          'Increase your income and customer happiness by offering related services, like post-groom paw massages, that keep them coming back.',
      },
    ],
  },
];

const TestimoninalTitle = () => {
  return (
    <TextWithHighlight>
      <h2>What groomers think of our Savvy alternative…</h2>
    </TextWithHighlight>
  );
};

const TestimoninalSubTitle = () => {
  return (
    <p style={{ maxWidth: '850px' }}>
      Need a second opinion while you compare pet grooming software? If anyone’s got a keen eye, it’s groomers
      themselves! Here’s why they love using Pata to manage bookings, reduce admin and grow their pet business.
    </p>
  );
};

const SignUpProcessTitle = () => {
  return (
    <SignUpProcessTitleContainer>
      <h2>
        Be part of the growing community of UK pet care professionals moving to Pata, the Savvy software alternative.
      </h2>
      <p>
        Looking for a grooming salon booking system with easy-to-use features? Try Pata! We help pros like you to reduce
        no-shows, simplify admin, and take seamless payments.
      </p>
    </SignUpProcessTitleContainer>
  );
};

export default function BusinessPataVsSavvy() {
  return (
    <BusinessCompetitor
      faqsConfig={savvyFaqsConfig}
      highlevelBenefitsConfig={highlevelBenefitsConfig}
      heroColor={'#26948f'}
      signUpProcessTitle={SignUpProcessTitle}
      testimoninalConfig={savvyTestimoninalConfig}
      testimoninalTitle={TestimoninalTitle}
      testimoninalSubTitle={TestimoninalSubTitle}
      heroConfig={savvyHeroConfig}
      featuresTiles={savvyFeaturesTiles}
      featuresTitle={'Pata vs Savvy: What makes our booking software different?'}
    />
  );
}
